import { useSyncExternalStore } from 'react';

/**
 * Global preference for which channel-links actions (Chat System / Channel Details /
 * Open stats / Edit Channel / Campaign History) are surfaced as quick buttons under the
 * channel name in the influencer dashboard table. Toggled from the checkboxes inside
 * ChannelLinksPopover and consumed by InfluencerRow. Persisted to localStorage so it
 * survives across sessions/tabs.
 */
export type NameColumnButtonKey = 'chat' | 'details' | 'stats' | 'edit' | 'history';
export type NameColumnButtonPrefs = Record<NameColumnButtonKey, boolean>;

const STORAGE_KEY = 'influencers:nameColumnButtons';
// Chat button is shown by default; the others are opt-in.
const DEFAULT_PREFS: NameColumnButtonPrefs = { chat: true, details: false, stats: false, edit: false, history: false };

function read(): NameColumnButtonPrefs {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_PREFS;
    const parsed = JSON.parse(raw) as Partial<NameColumnButtonPrefs>;
    return {
      chat: parsed.chat ?? DEFAULT_PREFS.chat,
      details: parsed.details ?? DEFAULT_PREFS.details,
      stats: parsed.stats ?? DEFAULT_PREFS.stats,
      edit: parsed.edit ?? DEFAULT_PREFS.edit,
      history: parsed.history ?? DEFAULT_PREFS.history,
    };
  } catch {
    return DEFAULT_PREFS;
  }
}

let prefs: NameColumnButtonPrefs = read();
const listeners = new Set<() => void>();

function emit(): void {
  listeners.forEach((l) => l());
}

/** Flip one action on/off, persist, and notify subscribers. */
export function toggleNameColumnButton(key: NameColumnButtonKey): void {
  prefs = { ...prefs, [key]: !prefs[key] };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    /* ignore quota / private-mode errors */
  }
  emit();
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

function getSnapshot(): NameColumnButtonPrefs {
  return prefs;
}

// Keep other tabs in sync when the preference changes elsewhere.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== STORAGE_KEY) return;
    prefs = read();
    emit();
  });
}

export function useNameColumnButtons(): NameColumnButtonPrefs {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
